'use client';
import React from 'react';

const PASSAGE_TEXT = [
  {
    id: 'para-A',
    label: 'Paragraph A',
    text: `Urban beekeeping has grown steadily in European cities over the past decade, with municipal authorities in cities such as Paris, Berlin, and London actively encouraging the installation of rooftop hives as part of broader urban greening initiatives. The number of registered hives in major European capitals has more than doubled since 2015, driven by a combination of public enthusiasm for local food production, growing awareness of global pollinator decline, and the romantic appeal of maintaining a direct connection with natural processes in an urban environment. Beekeeping associations report record membership figures, and waiting lists for introductory courses now extend to twelve months in some cities.`,
  },
  {
    id: 'para-B',
    label: 'Paragraph B',
    text: `However, the increase in hive numbers has not been matched by an equivalent rise in wild pollinator populations, which some ecologists argue depend on a broader range of flowering species than cities typically provide. While honeybees are generalist foragers capable of travelling several kilometres in search of nectar, wild bee species — including bumblebees, solitary bees, and hoverflies — tend to have narrower habitat requirements and shorter foraging ranges. The presence of large managed honeybee populations may, in some circumstances, intensify competition for the limited floral resources available within urban green spaces, potentially disadvantaging species that are already under pressure from habitat fragmentation outside city boundaries.`,
  },
  {
    id: 'para-C',
    label: 'Paragraph C',
    text: `The ecological consequences of this dynamic remain contested among researchers. Some studies conducted in Berlin and Bristol have found no significant negative correlation between honeybee density and wild bee abundance, suggesting that urban floral diversity may be sufficient to support both. Others point to evidence from high-density hive areas where certain solitary bee populations have shown measurable declines coinciding with rapid increases in managed hive numbers. What is broadly agreed upon is that the solution — to the extent one is needed — lies not in restricting urban beekeeping but in substantially expanding the diversity and continuity of flowering plant provision across urban landscapes, through measures such as converting mown grass verges to wildflower meadows and requiring flowering plant coverage in new building developments.`,
  },
];

function highlightText(text: string, highlight: string): React.ReactNode {
  if (!highlight) return text;
  const idx = text.toLowerCase().indexOf(highlight.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="passage-highlight-correct px-0.5 rounded">{text.slice(idx, idx + highlight.length)}</mark>
      {text.slice(idx + highlight.length)}
    </>
  );
}

export default function WalkthroughPassagePanel({
  highlightedParagraph,
  highlightedText,
}: {
  highlightedParagraph: string;
  highlightedText: string;
}) {
  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Panel header */}
      <div className="flex-shrink-0 px-3 sm:px-4 py-2 sm:py-2.5 bg-muted/50 border-b border-border">
        <p className="text-xs font-semibold text-foreground leading-snug">Passage 1 — Urban Beekeeping and Wild Pollinators</p>
        <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">Highlighted text marks the answer location for the current question</p>
      </div>

      {/* Scrollable passage body */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-3 sm:px-5 py-3 sm:py-5 space-y-4 sm:space-y-5">
        {PASSAGE_TEXT.map((para) => {
          const isActive = para.label.includes(highlightedParagraph);
          return (
            <div
              key={para.id}
              className={`rounded-lg transition-all duration-200 ${
                isActive ? 'ring-2 ring-primary/30 bg-secondary/20 p-2.5 sm:p-3 -mx-0.5' : ''
              }`}
            >
              <p className={`text-xs font-bold mb-1.5 sm:mb-2 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                {para.label}
                {isActive && (
                  <span className="ml-2 text-primary text-xs font-normal">(answer location)</span>
                )}
              </p>
              <p className="text-sm sm:text-sm text-foreground leading-relaxed sm:leading-relaxed">
                {isActive ? highlightText(para.text, highlightedText) : para.text}
              </p>
            </div>
          );
        })}

        {/* Legend */}
        <div className="flex items-center gap-3 pt-2 sm:pt-3 border-t border-border text-xs">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-4 h-3 passage-highlight-correct rounded" />
            <span className="text-muted-foreground">Answer location</span>
          </div>
        </div>
      </div>
    </div>
  );
}